import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Upload,
  Eye,
  Download,
  PenTool,
  Type,
  Save,
  Trash2,
  RotateCcw,
  Shield,
  Key,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Hash,
  EyeOff,
} from "lucide-react";
import {
  useDigitalCertificatesProfileData,
  DigitalCertificatesProfileTabProps,
} from "@/lib/profile/digitalCertificatesProfileData";
import DigitalCertificateProfileSkeleton from "./skeletons/DigitalCertificatesProfileSkeleton";
import { useState } from "react";
import { Box, Stack } from "@mui/system";
import { Skeleton } from "@mui/material";

export default function DigitalCertificatesProfileTab({
  open,
  activeTab,
  setErrorModalOpen,
  setDeleteModalOpen,
  setErrorMessage,
  setDeleteAction,
  setDeleteMessage,
}: DigitalCertificatesProfileTabProps) {
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [certificateToRevoke, setCertificateToRevoke] = useState<string | null>(
    null,
  );
  const [certificateNameToRevoke, setCertificateNameToRevoke] = useState<
    string | null
  >(null);

  const {
    isDarkMode,
    fontOptions,
    certificateRequest,
    setCertificateRequest,
    downloadCertificate,
    revokeCertificate,
    prescriptionPIN,
    pinSetup,
    setPinSetup,
    onRemovePIN,
    validatePIN,
    signatureData,
    setSignatureData,
    setCanvasElement,
    startDrawing,
    draw,
    stopDrawing,
    clearCanvas,
    handleSaveSignature,
    onClearSignature,
    resetCertificateRequest,
    generateNewCertificate,
    certificates,
    resetPinSetup,
    setupPIN,
    changePIN,
    userSignature,
    isPinCodeLoading,
    isAddDigitalSignatureLoading,
    isDeleteDigitalSignatureLoading,
    isCertificateLoading,
    userDigitalCertificate,
    setCertificates,
  } = useDigitalCertificatesProfileData({
    open,
    activeTab,
    setErrorModalOpen,
    setDeleteModalOpen,
    setErrorMessage,
    setDeleteAction,
    setDeleteMessage,
  });

  const handleRevokeClick = (certId: string, certName: string) => {
    setCertificateToRevoke(certId);
    setCertificateNameToRevoke(certName);
    setRevokeModalOpen(true);
  };

  const confirmRevoke = () => {
    if (certificateToRevoke) {
      revokeCertificate(certificateToRevoke);
      setCertificates([]);
      setRevokeModalOpen(false);
      setCertificateToRevoke(null);
      setCertificateNameToRevoke(null);
    }
  };

  const cancelRevoke = () => {
    setRevokeModalOpen(false);
    setCertificateToRevoke(null);
    setCertificateNameToRevoke(null);
  };

  return (
    <>
      <div className="space-y-8">
        {/* Digital Certificates Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Prescription Signing Certificates
              </h3>
              <p
                className={`text-sm mt-1 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Digital certificates for secure prescription Authorisation
              </p>
            </div>
            <Button
              onClick={generateNewCertificate}
              disabled={isCertificateLoading}
              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
            >
              <Key className="w-4 h-4" />
              {isCertificateLoading
                ? "Generating..."
                : "Generate New Certificate"}
            </Button>
          </div>

          {/* Existing Certificates */}
          <div className="space-y-3 mb-6">
            {isCertificateLoading ? (
              <>
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    padding: 2,
                    backgroundColor: "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Left Section */}
                  <Stack direction="row" spacing={2}>
                    {/* Avatar */}
                    <Skeleton variant="circular" width={40} height={40} />

                    <Stack spacing={1}>
                      {/* Name + Status */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Skeleton variant="text" width={160} height={20} />
                        <Skeleton variant="rounded" width={56} height={20} />
                      </Stack>

                      {/* Metadata */}
                      <Skeleton variant="text" width={260} />
                      <Skeleton variant="text" width={220} />
                      <Skeleton variant="text" width={200} />
                      <Skeleton variant="text" width={300} />
                      <Skeleton variant="text" width={340} />
                    </Stack>
                  </Stack>

                  {/* Right Actions */}
                  <Stack spacing={1}>
                    <Skeleton variant="rounded" width={96} height={32} />
                    <Skeleton variant="rounded" width={96} height={32} />
                  </Stack>
                </Box>
              </>
            ) : certificates && certificates.length > 0 ? (
              certificates?.map((cert) => (
                <div
                  key={cert.id}
                  className={`border rounded-lg p-4 transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                          cert.status === "active"
                            ? "bg-green-100 dark:bg-green-900/30"
                            : cert.status === "expired"
                              ? "bg-red-100 dark:bg-red-900/30"
                              : "bg-gray-100 dark:bg-gray-700"
                        }`}
                      >
                        <Shield
                          className={`w-5 h-5 ${
                            cert.status === "active"
                              ? "text-green-600 dark:text-green-400"
                              : cert.status === "expired"
                                ? "text-red-600 dark:text-red-400"
                                : "text-gray-600 dark:text-gray-400"
                          }`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4
                            className={`font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {cert.commonName}
                          </h4>
                          <Badge
                            className={
                              cert.status === "active"
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : cert.status === "expired"
                                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400"
                            }
                          >
                            {cert.status.charAt(0).toUpperCase() +
                              cert.status.slice(1)}
                          </Badge>
                        </div>
                        <div
                          className={`text-sm space-y-1 transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          <div>Serial: {cert.serialNumber}</div>
                          <div>
                            Issued:{" "}
                            {new Date(cert.issueDate).toLocaleDateString(
                              "en-GB",
                            )}
                          </div>
                          <div>
                            Expires:{" "}
                            {new Date(cert.expiryDate).toLocaleDateString(
                              "en-GB",
                            )}
                          </div>
                          <div>Issuer: {cert.issuer}</div>
                          <div className="font-mono text-xs break-all">
                            Fingerprint: {cert.fingerprint}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadCertificate(cert)}
                        className="flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </Button>
                      {cert.status === "active" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleRevokeClick(cert.id, cert.commonName)
                          }
                          className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No certificates available</p>
            )}
          </div>

          {/* Certificate Generation Process */}
          {certificateRequest.step > 1 && (
            <div
              className={`border rounded-lg p-6 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <Key className="w-5 h-5 text-blue-600" />
                <h4
                  className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Certificate Generation Process
                </h4>
              </div>

              {/* Step 1: Personal Information */}
              {certificateRequest.step === 1 && (
                <div className="space-y-4">
                  <h5
                    className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Step 1: Personal Information
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Full Name
                      </Label>
                      <Input
                        value={certificateRequest.personalInfo.fullName}
                        onChange={(e) =>
                          setCertificateRequest((prev) => ({
                            ...prev,
                            personalInfo: {
                              ...prev.personalInfo,
                              fullName: e.target.value,
                            },
                          }))
                        }
                        placeholder="Enter your full name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Registration Number
                      </Label>
                      <Input
                        value={
                          certificateRequest.personalInfo.registrationNumber
                        }
                        onChange={(e) =>
                          setCertificateRequest((prev) => ({
                            ...prev,
                            personalInfo: {
                              ...prev.personalInfo,
                              registrationNumber: e.target.value,
                            },
                          }))
                        }
                        placeholder="e.g., GPhC: 1234567"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Organization
                      </Label>
                      <Input
                        value={certificateRequest.personalInfo.organization}
                        onChange={(e) =>
                          setCertificateRequest((prev) => ({
                            ...prev,
                            personalInfo: {
                              ...prev.personalInfo,
                              organization: e.target.value,
                            },
                          }))
                        }
                        placeholder="Your pharmacy/organization"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Email Address
                      </Label>
                      <Input
                        type="email"
                        value={certificateRequest.personalInfo.email}
                        onChange={(e) =>
                          setCertificateRequest((prev) => ({
                            ...prev,
                            personalInfo: {
                              ...prev.personalInfo,
                              email: e.target.value,
                            },
                          }))
                        }
                        placeholder="your.email@example.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={() =>
                      setCertificateRequest((prev) => ({ ...prev, step: 2 }))
                    }
                    disabled={
                      !certificateRequest.personalInfo.fullName ||
                      !certificateRequest.personalInfo.registrationNumber ||
                      !certificateRequest.personalInfo.email
                    }
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Continue to Verification
                  </Button>
                </div>
              )}

              {/* Step 2: Document Verification */}
              {certificateRequest.step === 2 && (
                <div className="space-y-4">
                  <h5
                    className={`font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Step 2: Document Verification
                  </h5>
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Upload the required documents to verify your identity and
                    professional registration
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Identity Document
                      </Label>
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            document.getElementById("identity-doc")?.click()
                          }
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload ID Document
                        </Button>
                        <input
                          id="identity-doc"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setCertificateRequest((prev) => ({
                                ...prev,
                                verification: {
                                  ...prev.verification,
                                  identityDocument: file,
                                },
                              }));
                            }
                          }}
                        />
                        {certificateRequest.verification.identityDocument && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓{" "}
                            {
                              certificateRequest.verification.identityDocument
                                .name
                            }
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label
                        className={`transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Registration Certificate
                      </Label>
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            document.getElementById("reg-cert")?.click()
                          }
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Registration
                        </Button>
                        <input
                          id="reg-cert"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setCertificateRequest((prev) => ({
                                ...prev,
                                verification: {
                                  ...prev.verification,
                                  registrationCertificate: file,
                                },
                              }));
                            }
                          }}
                        />
                        {certificateRequest.verification
                          .registrationCertificate && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓{" "}
                            {
                              certificateRequest.verification
                                .registrationCertificate.name
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() =>
                        setCertificateRequest((prev) => ({ ...prev, step: 1 }))
                      }
                    >
                      Back
                    </Button>
                    <Button
                      // onClick={generateCertificate}
                      // disabled={
                      //   !certificateRequest.verification.identityDocument ||
                      //   !certificateRequest.verification.registrationCertificate
                      // }
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Generate Certificate
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Generating */}
              {certificateRequest.step === 3 &&
                certificateRequest.isGenerating && (
                  <div className="text-center py-8">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                    <h5
                      className={`font-medium mb-2 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      Generating Your Certificate
                    </h5>
                    <p
                      className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Please wait while we generate your digital certificate...
                    </p>
                  </div>
                )}

              {/* Step 4: Complete */}
              {certificateRequest.step === 4 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h5
                    className={`font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    Certificate Generated Successfully!
                  </h5>
                  <p
                    className={`text-sm mb-4 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Your prescription signing certificate has been created and
                    is ready to use.
                  </p>
                  <Button
                    onClick={resetCertificateRequest}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Done
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Prescription Sign-off PIN Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3
                className={`text-lg font-semibold transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Prescription Sign-off PIN
              </h3>
              <p
                className={`text-sm mt-1 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Secure 4-digit PIN for final prescription Authorisation
              </p>
            </div>
            {!prescriptionPIN.isSet ? (
              <Button
                onClick={() =>
                  setPinSetup((prev) => ({ ...prev, showSetup: true }))
                }
                className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                disabled={userDigitalCertificate === null}
              >
                <Hash className="w-4 h-4" />
                Create PIN
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    setPinSetup((prev) => ({ ...prev, showSetup: true }))
                  }
                  className="flex items-center gap-2"
                >
                  <Key className="w-4 h-4" />
                  Change PIN
                </Button>
                <Button
                  variant="outline"
                  onClick={onRemovePIN}
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                >
                  Remove PIN
                </Button>
              </div>
            )}
          </div>

          {/* Current PIN Status */}
          {prescriptionPIN.isSet && (
            <div
              className={`border rounded-lg p-4 mb-6 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                    prescriptionPIN.isTemporarilyLocked
                      ? "bg-red-100 dark:bg-red-900/30"
                      : "bg-green-100 dark:bg-green-900/30"
                  }`}
                >
                  <Hash
                    className={`w-5 h-5 ${
                      prescriptionPIN.isTemporarilyLocked
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4
                      className={`font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Prescription PIN Active
                    </h4>
                    <Badge
                      className={
                        prescriptionPIN.isTemporarilyLocked
                          ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                          : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      }
                    >
                      {prescriptionPIN.isTemporarilyLocked
                        ? "Temporarily Locked"
                        : "Active"}
                    </Badge>
                  </div>
                  {/* <div
                                        className={`text-sm space-y-1 transition-colors duration-300 ${isDarkMode ? "text-gray-300" : "text-gray-600"
                                            }`}
                                    >
                                        <div>
                                            Created:{" "}
                                            {new Date(prescriptionPIN.createdDate).toLocaleDateString()}
                                        </div>
                                        <div>
                                            Last Used:{" "}
                                            {prescriptionPIN.lastUsed
                                                ? new Date(prescriptionPIN.lastUsed).toLocaleDateString()
                                                : "Never"}
                                        </div>
                                        <div>Usage Count: {prescriptionPIN.usageCount}</div>
                                        {prescriptionPIN.failedAttempts > 0 && (
                                            <div className="text-amber-600 dark:text-amber-400">
                                                Failed Attempts: {prescriptionPIN.failedAttempts}
                                            </div>
                                        )}
                                    </div> */}
                </div>
              </div>
            </div>
          )}

          {/* PIN Setup/Change Form */}
          {pinSetup.showSetup && (
            <div
              className={`border rounded-lg p-6 mb-6 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                <Hash className="w-5 h-5 text-blue-600" />
                <h4
                  className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {prescriptionPIN.isSet ? "Change PIN" : "Create PIN"}
                </h4>
              </div>

              {pinSetup.step === 1 && (
                <div className="space-y-4">
                  {prescriptionPIN.isSet && (
                    <div>
                      <Label
                        className={`block mb-2 font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                      >
                        Current PIN
                      </Label>
                      <div className="relative max-w-xs">
                        <Input
                          type={pinSetup.showPin ? "text" : "password"}
                          onChange={(e) =>
                            setPinSetup((prev) => ({
                              ...prev,
                              currentPin: e.target.value,
                            }))
                          }
                          placeholder="Enter current PIN"
                          maxLength={4}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setPinSetup((prev) => ({
                              ...prev,
                              showPin: !prev.showPin,
                            }))
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {pinSetup.showPin ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label
                      className={`block mb-2 font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      {prescriptionPIN.isSet ? "New PIN" : "Create PIN"}
                    </Label>
                    <div className="relative max-w-xs">
                      <Input
                        type={pinSetup.showPin ? "text" : "password"}
                        value={pinSetup.newPin}
                        onChange={(e) =>
                          setPinSetup((prev) => ({
                            ...prev,
                            newPin: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4),
                          }))
                        }
                        placeholder="Enter 4-digit PIN"
                        maxLength={4}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setPinSetup((prev) => ({
                            ...prev,
                            showPin: !prev.showPin,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        {pinSetup.showPin ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {pinSetup.newPin && !validatePIN(pinSetup.newPin).valid && (
                      <p className="text-red-500 text-xs mt-1">
                        {validatePIN(pinSetup.newPin).message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      className={`block mb-2 font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                    >
                      Confirm PIN
                    </Label>
                    <div className="relative max-w-xs">
                      <Input
                        type={pinSetup.showPin ? "text" : "password"}
                        value={pinSetup.confirmPin}
                        onChange={(e) =>
                          setPinSetup((prev) => ({
                            ...prev,
                            confirmPin: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 4),
                          }))
                        }
                        placeholder="Confirm 4-digit PIN"
                        maxLength={4}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setPinSetup((prev) => ({
                            ...prev,
                            showPin: !prev.showPin,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        {pinSetup.showPin ? (
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                    {pinSetup.confirmPin &&
                      pinSetup.newPin !== pinSetup.confirmPin && (
                        <p className="text-red-500 text-xs mt-1">
                          PINs do not match
                        </p>
                      )}
                  </div>

                  <div
                    className={`p-4 rounded-lg transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-blue-900/20 border border-blue-700"
                        : "bg-blue-50 border border-blue-200"
                    }`}
                  >
                    <h5
                      className={`font-medium mb-2 transition-colors duration-300 ${
                        isDarkMode ? "text-blue-300" : "text-blue-800"
                      }`}
                    >
                      PIN Security Guidelines
                    </h5>
                    <ul
                      className={`text-sm space-y-1 transition-colors duration-300 ${
                        isDarkMode ? "text-blue-200" : "text-blue-700"
                      }`}
                    >
                      <li>• Must be exactly 4 digits</li>
                      <li>• Cannot be sequential (1234) or repeated (1111)</li>
                      <li>• Avoid obvious combinations (0000, 1234)</li>
                      <li>
                        • Will be required for every prescription sign-off
                      </li>
                      <li>• Account will be locked after 3 failed attempts</li>
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={resetPinSetup}>
                      Cancel
                    </Button>
                    <Button
                      onClick={prescriptionPIN.isSet ? changePIN : setupPIN}
                      disabled={
                        !pinSetup.newPin ||
                        !pinSetup.confirmPin ||
                        pinSetup.newPin !== pinSetup.confirmPin ||
                        !validatePIN(pinSetup.newPin).valid ||
                        (prescriptionPIN.isSet && !pinSetup.currentPin) ||
                        isPinCodeLoading
                      }
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {prescriptionPIN.isSet
                        ? isPinCodeLoading
                          ? "Changing..."
                          : "Change PIN"
                        : isPinCodeLoading
                          ? "Creating..."
                          : "Create PIN"}
                    </Button>
                  </div>
                </div>
              )}

              {pinSetup.step === 3 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h5
                    className={`font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-800"
                    }`}
                  >
                    PIN {prescriptionPIN.isSet ? "Changed" : "Created"}{" "}
                    Successfully!
                  </h5>
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Your prescription sign-off PIN is ready to use.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* PIN Usage Information */}
          {!pinSetup.showSetup && (
            <div
              className={`p-4 rounded-lg transition-colors duration-300 ${
                isDarkMode
                  ? "bg-amber-900/20 border border-amber-700"
                  : "bg-amber-50 border border-amber-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <AlertTriangle
                  className={`w-5 h-5 mt-0.5 transition-colors duration-300 ${
                    isDarkMode ? "text-amber-400" : "text-amber-600"
                  }`}
                />
                <div>
                  <h5
                    className={`font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? "text-amber-300" : "text-amber-800"
                    }`}
                  >
                    Important: Prescription Sign-off Security
                  </h5>
                  <p
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-amber-200" : "text-amber-700"
                    }`}
                  >
                    This PIN will be required to finalize and authorise all
                    prescriptions. Keep it secure and do not share it with
                    anyone. The PIN serves as your digital signature for
                    prescription approval and ensures compliance with medical
                    Authorisation requirements.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Digital Signature Section */}
        <div>
          <h3
            className={`text-lg font-semibold mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Digital Signature
          </h3>
          <p
            className={`text-sm mb-6 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-600"
            }`}
          >
            Create your digital signature by drawing or typing your name
          </p>

          {/* Mode Selection */}
          <div className="mb-6">
            <Label
              className={`block mb-3 font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-900"
              }`}
            >
              Signature Method
            </Label>
            <div className="flex gap-4">
              <Button
                variant={signatureData.mode === "draw" ? "default" : "outline"}
                onClick={() =>
                  setSignatureData((prev) => ({ ...prev, mode: "draw" }))
                }
                className="flex items-center gap-2"
              >
                <PenTool className="w-4 h-4" />
                Draw Signature
              </Button>
              <Button
                variant={signatureData.mode === "type" ? "default" : "outline"}
                onClick={() =>
                  setSignatureData((prev) => ({ ...prev, mode: "type" }))
                }
                className="flex items-center gap-2"
              >
                <Type className="w-4 h-4" />
                Type Signature
              </Button>
            </div>
          </div>

          {/* Drawing Mode */}
          {signatureData.mode === "draw" && (
            <div className="space-y-4">
              <Label
                className={`block font-medium transition-colors duration-300 ${
                  isDarkMode ? "text-gray-200" : "text-gray-900"
                }`}
              >
                Draw Your Signature
              </Label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-800"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <canvas
                  ref={setCanvasElement}
                  width={500}
                  height={200}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  className={`border rounded cursor-crosshair transition-colors duration-300 ${
                    isDarkMode
                      ? "border-gray-600 bg-gray-900"
                      : "border-gray-300 bg-white"
                  }`}
                  style={{ maxWidth: "100%" }}
                />
                <div className="flex justify-between items-center mt-3">
                  <p
                    className={`text-xs transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Click and drag to draw your signature
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearCanvas}
                    className="flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Typing Mode */}
          {signatureData.mode === "type" && (
            <div className="space-y-4">
              <div>
                <Label
                  htmlFor="signatureName"
                  className={`block mb-2 font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Your Name
                </Label>
                <Input
                  id="signatureName"
                  value={signatureData.typedName}
                  onChange={(e) =>
                    setSignatureData((prev) => ({
                      ...prev,
                      typedName: e.target.value,
                    }))
                  }
                  placeholder="Enter your full name"
                  className="max-w-md"
                />
              </div>

              <div>
                <Label
                  className={`block mb-2 font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Font Style
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                  {fontOptions.map((font) => (
                    <button
                      key={font.value}
                      onClick={() =>
                        setSignatureData((prev) => ({
                          ...prev,
                          selectedFont: font.value,
                        }))
                      }
                      className={`p-4 border rounded-lg text-left transition-all duration-300 ${
                        signatureData.selectedFont === font.value
                          ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50 dark:bg-blue-950"
                          : isDarkMode
                            ? "border-gray-600 hover:border-gray-500 bg-gray-800"
                            : "border-gray-300 hover:border-gray-400 bg-white"
                      }`}
                    >
                      <div
                        className={`text-2xl mb-1 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-200" : "text-gray-900"
                        }`}
                        style={{
                          fontFamily: font.family,
                          fontSize: `${signatureData.fontSize}px`,
                        }}
                      >
                        {signatureData.typedName || "Your Name"}
                      </div>
                      <div
                        className={`text-xs transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {font.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label
                  className={`block mb-2 font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Font Size: {signatureData.fontSize}px
                </Label>
                <input
                  type="range"
                  min="20"
                  max="50"
                  value={signatureData.fontSize}
                  onChange={(e) =>
                    setSignatureData((prev) => ({
                      ...prev,
                      fontSize: parseInt(e.target.value),
                    }))
                  }
                  className="w-full max-w-xs"
                />
              </div>

              {/* Preview */}
              {signatureData.typedName && (
                <div>
                  <Label
                    className={`block mb-2 font-medium transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Signature Preview
                  </Label>
                  <div
                    className={`border rounded-lg p-6 text-center transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-800"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    <div
                      className={`transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-900"
                      }`}
                      style={{
                        fontFamily: fontOptions.find(
                          (f) => f.value === signatureData.selectedFont,
                        )?.family,
                        fontSize: `${signatureData.fontSize}px`,
                      }}
                    >
                      {signatureData.typedName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSaveSignature}
              disabled={
                (signatureData.mode === "draw" &&
                  !signatureData.drawnSignature) ||
                (signatureData.mode === "type" &&
                  !signatureData.typedName.trim()) ||
                isDeleteDigitalSignatureLoading ||
                isAddDigitalSignatureLoading
              }
              className="bg-slate-900 hover:bg-slate-800 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isAddDigitalSignatureLoading ? "Saving..." : "Save Signature"}
            </Button>
            {signatureData.mode === "draw" && (
              <Button
                onClick={() => onClearSignature()}
                disabled={
                  (signatureData.mode === "draw" &&
                    !signatureData.drawnSignature) ||
                  (signatureData.mode === "type" &&
                    !signatureData.typedName.trim()) ||
                  isDeleteDigitalSignatureLoading ||
                  isAddDigitalSignatureLoading ||
                  userSignature === null
                }
                variant="outline"
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleteDigitalSignatureLoading ? "Clearing..." : "Clear"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Revoke Confirmation Modal */}
      {revokeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg p-6 w-full max-w-md transition-colors duration-300 ${
              isDarkMode
                ? "bg-gray-800 border border-gray-700"
                : "bg-white border border-gray-200"
            }`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3
                  className={`text-lg font-semibold transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Revoke Certificate
                </h3>
                <p
                  className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  Are you sure you want to revoke this certificate?
                </p>
              </div>
            </div>

            <div
              className={`mb-6 p-4 rounded-lg transition-colors duration-300 ${
                isDarkMode
                  ? "bg-red-900/20 border border-red-700"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <h4
                className={`font-medium mb-2 transition-colors duration-300 ${
                  isDarkMode ? "text-red-300" : "text-red-800"
                }`}
              >
                Warning: This action cannot be undone
              </h4>
              <ul
                className={`text-sm space-y-1 transition-colors duration-300 ${
                  isDarkMode ? "text-red-200" : "text-red-700"
                }`}
              >
                <li>• The certificate will be immediately invalidated</li>
                <li>
                  • You will no longer be able to sign prescriptions with this
                  certificate
                </li>
                <li>
                  • You will need to generate a new certificate to continue
                  signing prescriptions
                </li>
              </ul>
            </div>

            {certificateNameToRevoke && (
              <div
                className={`mb-6 p-3 rounded transition-colors duration-300 ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <p
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  Certificate to revoke:
                </p>
                <p
                  className={`font-medium transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {certificateNameToRevoke}
                </p>
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={cancelRevoke}
                className="border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRevoke}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, Revoke Certificate
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
